import { fetchWithAuth } from '@/utils/api-error-handler'

const API_URL = process.env.NEXT_PUBLIC_API_URL?.replace(/\/$/, '')

export type StructurePreview = {
  url: string
  format: 'cif' | 'pdb'
}

type ArtifactItem = {
  path: string
  sizeBytes: number | null
  url: string | null
}

export function isCompletedLikeStatus(status?: string | null) {
  const s = String(status || '').trim().toUpperCase()
  return s === 'COMPLETED' || s === 'SUCCEEDED' || s === 'SUCCESS' || s === 'READY'
}

export function isStructurePreviewWorkflowType(workflowType?: string | null) {
  const t = String(workflowType || '').trim().toLowerCase()
  return t === 'fold_job' || t === 'calvados_openmm_v1' || t === 'openmmdl_v1' || t === 'boltzgen_v1'
}

export function inferStructureFormat(urlOrFileName: string): 'cif' | 'pdb' {
  const lower = String(urlOrFileName || '').trim().toLowerCase()
  return lower.endsWith('.pdb') ? 'pdb' : 'cif'
}

export function withTeamId(path: string, teamId?: string | null) {
  const normalizedTeamId = String(teamId || '').trim()
  if (!normalizedTeamId) return path
  try {
    const parsed = new URL(path, typeof window !== 'undefined' ? window.location.origin : 'http://localhost')
    if (!parsed.searchParams.get('team_id')) {
      parsed.searchParams.set('team_id', normalizedTeamId)
    }
    if (/^https?:\/\//i.test(path)) return parsed.toString()
    return `${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    return path
  }
}

export function wrapStructureProxy(url: string) {
  return `/api/structure?url=${encodeURIComponent(url)}`
}

function normalizeArtifactPath(path: string) {
  return String(path || '')
    .trim()
    .replace(/^\.?\/*/, '')
}

function fallbackArtifactUrl(logUrl: string | null | undefined, relativePath: string): string | null {
  const cleanRel = normalizeArtifactPath(relativePath)
  if (!cleanRel) return null
  const raw = String(logUrl || '').trim()
  if (!raw) return null
  try {
    const parsed = new URL(raw)
    const pathParts = parsed.pathname.split('/').filter(Boolean)
    if (pathParts.length === 0) return null
    const baseParts = pathParts.slice(0, -1)
    const relParts = cleanRel.split('/').filter(Boolean).map((p) => encodeURIComponent(p))
    parsed.pathname = `/${[...baseParts, ...relParts].join('/')}`
    parsed.search = ''
    parsed.hash = ''
    return parsed.toString()
  } catch {
    return null
  }
}

function resolveArtifactUrl(item: ArtifactItem, logUrl: string | null | undefined): string | null {
  if (item.url) return item.url
  return fallbackArtifactUrl(logUrl, item.path)
}

function pickStructureItem(items: ArtifactItem[]) {
  const pdbs = items.filter((item) => item.path.toLowerCase().endsWith('.pdb'))
  if (pdbs.length === 0) return null
  const nonInputs = pdbs.filter((item) => !item.path.startsWith('_inputs/'))
  const top = nonInputs.find(
    (item) => item.path.toLowerCase().endsWith('/top.pdb') || item.path.toLowerCase() === 'top.pdb'
  )
  if (top) return top
  if (nonInputs[0]) return nonInputs[0]
  return pdbs[0] || null
}

export function extractFoldStructureSource(payload: unknown): StructurePreview | null {
  const cifs: StructurePreview[] = []
  const pdbs: StructurePreview[] = []
  const collect = (value: unknown) => {
    if (!value || typeof value !== 'object') return
    const row = value as Record<string, unknown>
    const cifUrl = String(row.cif_url || row.cifUrl || '').trim()
    const pdbUrl = String(row.pdb_url || row.pdbUrl || '').trim()
    // Ignore placeholder / non-URL filenames that never got signed.
    const looksRemote = (url: string) => /^https?:\/\//i.test(url) || url.startsWith('/api/')
    if (cifUrl && looksRemote(cifUrl)) cifs.push({ url: cifUrl, format: 'cif' })
    if (pdbUrl && looksRemote(pdbUrl)) pdbs.push({ url: pdbUrl, format: 'pdb' })
  }
  if (payload && typeof payload === 'object') {
    const row = payload as Record<string, unknown>
    collect(row.predictionPayload)
    collect(row)
    const sequences = Array.isArray(row.sequences) ? row.sequences : []
    for (const sequence of sequences) {
      if (sequence && typeof sequence === 'object') {
        const seq = sequence as Record<string, unknown>
        collect(seq.predictionPayload)
        collect(seq)
      }
    }
  }
  // Prefer CIF, but fall back to PDB when CIF is missing/unsigned (common for some ESM runs).
  return cifs[0] || pdbs[0] || null
}

async function fetchJsonWithAuth(path: string): Promise<unknown> {
  const response = await fetchWithAuth(path, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`)
  }
  return response.json()
}

/** Resolve a structure preview URL for a completed job/workflow. Returns null if none. */
export async function resolveJobStructurePreview(opts: {
  workflowId: string
  workflowType?: string | null
  teamId?: string | null
}): Promise<StructurePreview | null> {
  if (!API_URL) return null
  const workflowId = String(opts.workflowId || '').trim()
  if (!workflowId) return null
  const type = String(opts.workflowType || '').trim().toLowerCase()
  const teamId = opts.teamId
  const params = new URLSearchParams()
  if (teamId) params.set('team_id', teamId)
  const qs = params.toString()

  try {
    if (type === 'fold_job') {
      const payload = await fetchJsonWithAuth(
        `${API_URL}/v1/jobs/${encodeURIComponent(workflowId)}${qs ? `/results?${qs}` : '/results'}`
      )
      const source = extractFoldStructureSource(payload)
      if (!source?.url) return null
      return {
        url: withTeamId(wrapStructureProxy(source.url), teamId),
        format: source.format,
      }
    }

    if (type === 'calvados_openmm_v1' || type === 'openmmdl_v1') {
      const workflow = (await fetchJsonWithAuth(
        `${API_URL}/v1/workflows/${encodeURIComponent(workflowId)}${qs ? `?${qs}` : ''}`
      )) as Record<string, unknown>
      const tasks = Array.isArray(workflow.tasks) ? (workflow.tasks as Array<Record<string, unknown>>) : []
      const latestTask = tasks.length > 0 ? tasks[tasks.length - 1] : null
      const taskResult =
        latestTask && typeof latestTask.result_raw_json === 'object' && latestTask.result_raw_json
          ? (latestTask.result_raw_json as Record<string, unknown>)
          : {}
      const rawArtifacts = Array.isArray(taskResult.artifacts)
        ? (taskResult.artifacts as Array<Record<string, unknown>>)
        : []
      const artifactItems = rawArtifacts
        .map((raw) => {
          const path = normalizeArtifactPath(String(raw.path || ''))
          if (!path) return null
          const size = typeof raw.sizeBytes === 'number' && Number.isFinite(raw.sizeBytes) ? raw.sizeBytes : null
          const url = String(raw.url || '').trim() || null
          return { path, sizeBytes: size, url } satisfies ArtifactItem
        })
        .filter(Boolean) as ArtifactItem[]
      const structureItem = pickStructureItem(artifactItems)
      const structureSourceUrl = structureItem
        ? resolveArtifactUrl(structureItem, String(workflow.log_file_url || '').trim() || null)
        : null
      if (!structureSourceUrl) return null
      return {
        url: withTeamId(wrapStructureProxy(structureSourceUrl), teamId),
        format: 'pdb',
      }
    }

    if (type === 'boltzgen_v1') {
      const response = (await fetchJsonWithAuth(
        `${API_URL}/v1/workflows/task-results/${encodeURIComponent(workflowId)}${qs ? `?${qs}` : ''}`
      )) as Record<string, unknown>
      const tasksResults = Array.isArray(response.tasksResults)
        ? (response.tasksResults as Array<Record<string, unknown>>)
        : []
      const boltzgenRows = tasksResults
        .filter((task) => String(task.task_type || '').trim() === 'pipeline_run_boltzgen_v1')
        .flatMap((task) => (Array.isArray(task.parsed_results) ? task.parsed_results : []))
        .filter((row): row is Record<string, unknown> => Boolean(row && typeof row === 'object'))
        .map((row) => {
          const file = row.file && typeof row.file === 'object' ? (row.file as Record<string, unknown>) : {}
          return {
            finalRank: typeof row.final_rank === 'number' ? row.final_rank : Number(row.final_rank),
            libraryItemId: String(file.libraryItemId || '').trim(),
            fileName: String(file.fileName || '').trim(),
          }
        })
        .filter((row) => row.libraryItemId && row.fileName && row.fileName.toLowerCase().endsWith('.cif'))
        .sort((a, b) => {
          const left = Number.isFinite(a.finalRank) ? a.finalRank : Number.POSITIVE_INFINITY
          const right = Number.isFinite(b.finalRank) ? b.finalRank : Number.POSITIVE_INFINITY
          return left - right
        })
      const topRow = boltzgenRows[0]
      if (!topRow) return null
      return {
        url: withTeamId(
          `/api/structure?itemId=${encodeURIComponent(topRow.libraryItemId)}&fileName=${encodeURIComponent(topRow.fileName)}`,
          teamId
        ),
        format: inferStructureFormat(topRow.fileName),
      }
    }
  } catch {
    return null
  }
  return null
}

/** Run async work over items with a fixed concurrency limit. */
export async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results = new Array<R>(items.length)
  let next = 0
  const limit = Math.max(1, Math.min(concurrency, items.length || 1))
  const runners = Array.from({ length: limit }, async () => {
    while (next < items.length) {
      const index = next
      next += 1
      results[index] = await worker(items[index]!, index)
    }
  })
  await Promise.all(runners)
  return results
}
